import React from "react";
import { Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { FaUtensils } from "react-icons/fa";
import ImageDestinationBadge from "../ImageDestinationBadge/ImageDestinationBadge";
import CountryFlag from "../CountryFlag/CountryFlag";
import HotelPlaceholder from "../../../assets/images/card/hotel.svg";
import "./UnifiedHotelPackageCard.scss";

function UnifiedHotelPackageCard({
  hotelName,
  imageUrl,
  imageAlt,
  isFallbackImage = false,
  fallbackImageUrl = HotelPlaceholder,
  onMediaClick,
  destinationBadgeLabel,
  destinationCode,
  stars = 0,
  priceContent = null,
  listDisplayMeta = null,
  ctaLabel = "צפו בחבילה",
  onCtaClick,
  metaDestination,
  metaBoard,
  inclusions = null,
  variant = "overlay",
  footer,
  className = "",
}) {
  const isCommerce = variant === "commerce";
  const showMeta = !isCommerce && Boolean(metaDestination || metaBoard);
  const starCount = typeof stars === "number" && stars > 0 ? Math.min(stars, 5) : 0;
  const showPublicContract =
    listDisplayMeta &&
    typeof listDisplayMeta === "object" &&
    (listDisplayMeta.dateRangeLabel ||
      listDisplayMeta.nightsLabel ||
      listDisplayMeta.currencyDisplay?.primaryUsdLine);
  const heroPriceContent = showPublicContract ? null : priceContent;
  const boardLabel = String(inclusions?.boardLabel || metaBoard || "").trim();
  const flightsIncluded = Boolean(inclusions?.flightsIncluded);
  const openPackage = onCtaClick || onMediaClick;

  const handleCardActivate = (event) => {
    if (!openPackage) return;
    if (event?.target?.closest?.("button, a")) return;
    openPackage(event);
  };

  return (
    <article
      className={["hr-upc", isCommerce ? "hr-upc--commerce" : "", className]
        .filter(Boolean)
        .join(" ")}
      dir="rtl"
      onClick={isCommerce ? handleCardActivate : undefined}
      onKeyDown={
        isCommerce && openPackage
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openPackage(e);
              }
            }
          : undefined
      }
      role={isCommerce && openPackage ? "link" : undefined}
      tabIndex={isCommerce && openPackage ? 0 : undefined}
    >
      <div
        className={`hr-upc__media${isFallbackImage ? " hr-upc__media--fallback" : ""}`}
        onClick={!isCommerce ? onMediaClick : undefined}
        onKeyDown={
          !isCommerce && onMediaClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onMediaClick(e);
                }
              }
            : undefined
        }
        role={!isCommerce && onMediaClick ? "button" : undefined}
        tabIndex={!isCommerce && onMediaClick ? 0 : undefined}
        aria-label={!isCommerce && onMediaClick ? `פתח חבילה: ${hotelName}` : undefined}
      >
        <div className="hr-upc__media-bg">
          <img
            src={imageUrl}
            alt={imageAlt || hotelName}
            className="hr-upc__img"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              if (fallbackImageUrl && e.currentTarget.src !== fallbackImageUrl) {
                e.currentTarget.src = fallbackImageUrl;
              }
            }}
          />
        </div>
        {isCommerce ? <div className="hr-upc__gradient hr-upc__gradient--soft" aria-hidden /> : (
          <div className="hr-upc__gradient" aria-hidden />
        )}

        {destinationBadgeLabel ? (
          <div className="hr-upc__badge-location">
            <ImageDestinationBadge
              label={destinationBadgeLabel}
              destinationCode={destinationCode}
              className="hr-upc__destination-pill"
            />
          </div>
        ) : null}

        {flightsIncluded ? (
          <span className="hr-upc__flight-chip">טיסה כלולה</span>
        ) : null}

        {heroPriceContent != null && heroPriceContent !== false ? (
          <div className="hr-upc__price-badge">{heroPriceContent}</div>
        ) : null}

        {!isCommerce ? (
          <div className="hr-upc__title-block">
            <h3 className="hr-upc__hotel-name">{hotelName}</h3>
            {starCount > 0 ? (
              <div className="hr-upc__stars" aria-hidden>
                {"★".repeat(starCount)}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {isCommerce ? (
        <div className="hr-upc__commerce">
          <h3 className="hr-upc__commerce-title">{hotelName}</h3>
          {starCount > 0 ? (
            <div className="hr-upc__stars hr-upc__stars--body" aria-label={`${starCount} כוכבים`}>
              {"★".repeat(starCount)}
            </div>
          ) : null}
          <div className="hr-upc__facts">
            {listDisplayMeta?.dateRangeLabel ? (
              <span>{listDisplayMeta.dateRangeLabel}</span>
            ) : null}
            {listDisplayMeta?.nightsLabel ? (
              <span>{listDisplayMeta.nightsLabel}</span>
            ) : null}
          </div>
          {(boardLabel || flightsIncluded) ? (
            <div className="hr-upc__chips">
              {flightsIncluded ? <span className="hr-upc__chip">טיסה + מלון</span> : null}
              {boardLabel ? <span className="hr-upc__chip">{boardLabel}</span> : null}
            </div>
          ) : null}
          <div className="hr-upc__commerce-foot">
            {listDisplayMeta?.currencyDisplay?.primaryUsdLine ? (
              <div className="hr-upc__commerce-price">
                <span className="hr-upc__commerce-price-main" translate="no">
                  {listDisplayMeta.currencyDisplay.primaryUsdLine}
                  {listDisplayMeta.currencyDisplay.perPersonUnit ? (
                    <span className="hr-upc__pc-unit">
                      {listDisplayMeta.currencyDisplay.perPersonUnit}
                    </span>
                  ) : null}
                </span>
                {listDisplayMeta.currencyDisplay.totalUsdLine ||
                listDisplayMeta.currencyDisplay.perPersonHint ? (
                  <span className="hr-upc__commerce-price-hint" translate="no">
                    {listDisplayMeta.currencyDisplay.totalUsdLine ||
                      listDisplayMeta.currencyDisplay.perPersonHint}
                  </span>
                ) : null}
              </div>
            ) : null}
            <Button
              htmlType="button"
              type="primary"
              className="hr-upc__cta hr-upc__cta--compact"
              onClick={onCtaClick}
            >
              <span className="hr-upc__cta-text">{ctaLabel}</span>
              <ArrowLeftOutlined className="hr-upc__cta-icon" aria-hidden />
            </Button>
          </div>
        </div>
      ) : (
        <>
          {showPublicContract ? (
            <div className="hr-upc__public-contract">
              {listDisplayMeta.dateRangeLabel ? (
                <div className="hr-upc__pc-block">
                  <div className="hr-upc__pc-label">תאריכים</div>
                  <div className="hr-upc__pc-value hr-upc__pc-value--dates">
                    {listDisplayMeta.dateRangeLabel}
                  </div>
                </div>
              ) : null}
              {listDisplayMeta.nightsLabel ? (
                <div className="hr-upc__pc-block">
                  <div className="hr-upc__pc-label">לילות</div>
                  <div className="hr-upc__pc-value hr-upc__pc-value--stay">
                    {listDisplayMeta.nightsLabel}
                  </div>
                </div>
              ) : null}
              {listDisplayMeta.currencyDisplay?.primaryUsdLine ? (
                <div className="hr-upc__pc-block hr-upc__pc-block--price">
                  <div className="hr-upc__pc-value hr-upc__pc-value--usd-primary">
                    <span translate="no">{listDisplayMeta.currencyDisplay.primaryUsdLine}</span>
                    {listDisplayMeta.currencyDisplay.perPersonUnit ? (
                      <span className="hr-upc__pc-unit">
                        {listDisplayMeta.currencyDisplay.perPersonUnit}
                      </span>
                    ) : null}
                  </div>
                  {listDisplayMeta.currencyDisplay.totalUsdLine ||
                  listDisplayMeta.currencyDisplay.perPersonHint ? (
                    <div className="hr-upc__pc-hint" translate="no">
                      {listDisplayMeta.currencyDisplay.totalUsdLine ||
                        listDisplayMeta.currencyDisplay.perPersonHint}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="hr-upc__cta-bar">
            <Button
              htmlType="button"
              type="primary"
              size="large"
              block
              className="hr-upc__cta"
              onClick={onCtaClick}
            >
              <span className="hr-upc__cta-text">{ctaLabel}</span>
              <ArrowLeftOutlined className="hr-upc__cta-icon" aria-hidden />
            </Button>
          </div>

          {showMeta ? (
            <div className="hr-upc__meta">
              {metaDestination ? (
                <div className="hr-upc__meta-row">
                  <CountryFlag
                    destinationCode={destinationCode}
                    className="hr-country-flag--meta"
                    title={metaDestination}
                  />
                  <span className="hr-upc__meta-text">{metaDestination}</span>
                </div>
              ) : null}
              {metaBoard ? (
                <div className="hr-upc__meta-row">
                  <FaUtensils className="hr-upc__meta-icon" aria-hidden />
                  <span className="hr-upc__meta-text">{metaBoard}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}

      {footer ? <div className="hr-upc__footer">{footer}</div> : null}
    </article>
  );
}

export default UnifiedHotelPackageCard;
